/**
 * PATH: packages/quote-builder/src/runtime/quoteBrand.ts
 * FILE_ID: QB-RUNTIME-BRAND-001
 * PURPOSE: Reactive org quote-brand slot for the quote-builder package.
 * WHY: Lifted surfaces (cover page, export preview, KPI letterhead) used the
 *      master SPA's useQuoteBrand() which reads the tenant-bootstrap React
 *      context. A shared package cannot read a shell's context, so the shell
 *      publishes the resolved QuoteBrand into this slot (master:
 *      TenantBootstrap after the bootstrap fetch; hub apps: their own
 *      session bootstrap) and package components subscribe via
 *      useSyncExternalStore. Unprimed renders resolve to the neutral
 *      DEFAULT_QUOTE_BRAND — identical to the master hook's pre-bootstrap
 *      behaviour.
 * FLOW:
 *   ┌──────────────────┐   ┌───────────────────────────┐   ┌──────────────┐
 *   │ shell bootstrap  │ → │ setQuoteBuilderQuoteBrand │ → │ useQuoteBrand│
 *   │ (org + branding) │   │ (notifies listeners)      │   │ in components│
 *   └──────────────────┘   └───────────────────────────┘   └──────────────┘
 * RELATED:
 *   - src/app/tenant/useQuoteBrand.ts (master resolver — same mappers)
 *   - ./orgId.ts (same slot pattern)
 */

import { useSyncExternalStore } from 'react'

import { DEFAULT_QUOTE_BRAND } from '../branding/defaults'
import type { QuoteBrand } from '../branding/types'

let current: QuoteBrand = DEFAULT_QUOTE_BRAND
const listeners = new Set<() => void>()

/** Publish the org's resolved quote brand (shell bridges call on bootstrap). */
export function setQuoteBuilderQuoteBrand(brand: QuoteBrand | null): void {
  const next = brand ?? DEFAULT_QUOTE_BRAND
  if (next === current) return
  current = next
  listeners.forEach((l) => l())
}

/** Non-hook resolver for imperative call sites (print HTML builders). */
export function getQuoteBuilderQuoteBrand(): QuoteBrand {
  return current
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * The org's quote-document brand. Neutral (DEFAULT_QUOTE_BRAND) until the
 * shell primes the slot — consumers need no null handling. Keeps the same
 * name as the master hook so lifted call sites read identically.
 */
export function useQuoteBrand(): QuoteBrand {
  return useSyncExternalStore(subscribe, getQuoteBuilderQuoteBrand, () => current)
}
